import type {
  QuickSearchAggregateResponse,
  QuickSearchBody,
  QuickSearchCategoryName,
  QuickSearchCategoryPage,
  QuickSearchCategoryResultMap,
  QuickSearchEntity,
  QuickSearchFamilyRow,
  QuickSearchHierarchyResult,
  QuickSearchLanguage,
  QuickSearchModelRow,
  QuickSearchPage,
  QuickSearchProductEntity,
  QuickSearchProductResult,
  QuickSearchProductRow,
  QuickSearchResponse,
  QuickSearchSubdepartmentRow,
  QuickSearchSupermodelRow,
} from '@/types/searchTypes'
import * as apiBff from './bff'

function getQueryPath(season: string, endpoint: string, query: string, lang: QuickSearchLanguage, page?: number, pageSize?: number): string {
  const params = new URLSearchParams({ query, lang })
  if (page !== undefined)
    params.set('page', String(page))
  if (pageSize !== undefined)
    params.set('pageSize', String(pageSize))
  return `season/${season}/quick_search/${endpoint}?${params.toString()}`
}

function getAggregateQueryPath(season: string, query: string, lang: QuickSearchLanguage): string {
  const params = new URLSearchParams({ query, lang })
  return `season/${season}/quick_search?${params.toString()}`
}

function mapHierarchyResult(
  row: QuickSearchSubdepartmentRow | QuickSearchFamilyRow,
  entity: QuickSearchEntity,
  type: QuickSearchHierarchyResult['type'],
  familyIds: number[],
): QuickSearchHierarchyResult {
  return {
    id: `${type}-${entity.id}`,
    type,
    code: entity.id,
    labels: entity.labels,
    routeTarget: {
      universeId: row.universe.id,
      departmentId: row.department.id,
      subdepartmentId: row.subdepartment.id,
      familyIds,
    },
  }
}

function mapProductResult(
  row: QuickSearchProductRow,
  entity: QuickSearchProductEntity,
  type: QuickSearchProductResult['type'],
  viewMode: NonNullable<QuickSearchProductResult['routeTarget']['viewMode']>,
): QuickSearchProductResult {
  const id = String(entity.id)

  return {
    id: `${type}-${id}`,
    type,
    code: id,
    labels: entity.labels,
    image: entity.image,
    routeTarget: {
      universeId: row.universe.id,
      departmentId: row.department.id,
      subdepartmentId: row.subdepartment.id,
      familyIds: [row.family.id],
      viewMode,
    },
  }
}

function mapSubdepartments(rows: QuickSearchSubdepartmentRow[]): QuickSearchHierarchyResult[] {
  return rows.map(row => mapHierarchyResult(row, row.subdepartment, 'subdepartment', []))
}

function mapFamilies(rows: QuickSearchFamilyRow[]): QuickSearchHierarchyResult[] {
  return rows.map(row => mapHierarchyResult(row, row.family, 'family', [row.family.id]))
}

function mapSupermodels(rows: QuickSearchSupermodelRow[]): QuickSearchProductResult[] {
  return rows.map(row => mapProductResult(row, row.supermodel, 'supermodel', 'sm'))
}

function mapModels(rows: QuickSearchModelRow[]): QuickSearchProductResult[] {
  return rows.map(row => mapProductResult(row, row.model, 'model', 'model'))
}

interface QuickSearchCategoryRowMap {
  subdepartments: QuickSearchSubdepartmentRow
  families: QuickSearchFamilyRow
  supermodels: QuickSearchSupermodelRow
  models: QuickSearchModelRow
}

type QuickSearchCategoryRequest = QuickSearchBody & {
  page: number
  pageSize: number
  signal?: AbortSignal
}

type QuickSearchCategoryHandler<C extends QuickSearchCategoryName> = (
  request: QuickSearchCategoryRequest,
) => Promise<QuickSearchCategoryPage<C>>

function createCategoryHandler<C extends QuickSearchCategoryName>(
  category: C,
  endpoint: string,
  mapper: (rows: QuickSearchCategoryRowMap[C][]) => QuickSearchCategoryResultMap[C][],
): QuickSearchCategoryHandler<C> {
  return async ({ season, query, lang, page, pageSize, signal }) => {
    const response = await apiBff.callBFF<QuickSearchPage<QuickSearchCategoryRowMap[C]>>(
      'GET',
      getQueryPath(season, endpoint, query, lang, page, pageSize),
      undefined,
      { signal },
    )

    return {
      category,
      results: mapper(response.data),
      total: response.total_elements,
      totalPages: response.total_pages,
      pageIndex: response.page_request.page_index,
      pageSize: response.page_request.page_size,
    } as QuickSearchCategoryPage<C>
  }
}

const quickSearchCategoryHandlers = {
  subdepartments: createCategoryHandler('subdepartments', 'subdepartments', mapSubdepartments),
  families: createCategoryHandler('families', 'families', mapFamilies),
  supermodels: createCategoryHandler('supermodels', 'super-models', mapSupermodels),
  models: createCategoryHandler('models', 'models', mapModels),
} satisfies {
  [C in QuickSearchCategoryName]: QuickSearchCategoryHandler<C>
}

export async function getQuickSearch({ season, query, lang, signal }: QuickSearchBody & { signal?: AbortSignal }): Promise<QuickSearchResponse> {
  const response = await apiBff.callBFF<QuickSearchAggregateResponse>(
    'GET',
    getAggregateQueryPath(season, query, lang),
    undefined,
    { signal },
  )

  return {
    query,
    total: response.total,
    categories: {
      subdepartments: { total: response.subdepartments.total, results: mapSubdepartments(response.subdepartments.items) },
      families: { total: response.families.total, results: mapFamilies(response.families.items) },
      supermodels: { total: response.supermodels.total, results: mapSupermodels(response.supermodels.items) },
      models: { total: response.models.total, results: mapModels(response.models.items) },
    },
  }
}

export async function getQuickSearchCategory<C extends QuickSearchCategoryName>({
  season,
  query,
  lang,
  category,
  page,
  pageSize,
  signal,
}: QuickSearchBody & {
  category: C
  page: number
  pageSize: number
  signal?: AbortSignal
}): Promise<QuickSearchCategoryPage<C>> {
  const handler = quickSearchCategoryHandlers[category] as QuickSearchCategoryHandler<C>
  return handler({ season, query, lang, page, pageSize, signal })
}
